import { useCallback } from 'react';

import { WardrobeCategory } from '@/constants/enums';
import { useImageSourcePicker, type UseImageSourcePickerResult } from '@/hooks/useImageSourcePicker';
import useSubscribe from '@/hooks/useSubscribe';
import { isContextAvailable } from '@/miniappContext';
import { uploadBlobToCdn } from '@/services/cdnService';
import { processItemThumbnail } from '@/services/thumbnailProcessing';
import { getAppStore } from '@/store';
import type { WardrobeItem } from '@/types';
import analytics, { ScreenActionType } from '@/utils/analytics';
import showNotification, { NotificationType } from '@/utils/showNotification';

async function uploadFileToCdn(file: File): Promise<string> {
  if (!isContextAvailable()) return URL.createObjectURL(file);
  try {
    return await uploadBlobToCdn(file);
  } catch {
    return URL.createObjectURL(file);
  }
}

async function uploadUrlToCdn(url: string): Promise<string> {
  if (!isContextAvailable() || url.startsWith('http')) return url;
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await uploadBlobToCdn(blob);
  } catch {
    return url;
  }
}

/**
 * Pipeline: CDN upload -> update URLs -> i2i thumbnail processing -> update thumbnail.
 * The i2i restyle is skipped for the Complete Outfits category, where the user's original
 * full-look photo should be preserved as-is.
 */
function runUploadPipeline(itemId: string, getCdnUrl: () => Promise<string>, originalUrl: string, skipThumbnailProcessing: boolean) {
  getAppStore().setWardrobeItemProcessing(itemId, true);

  getCdnUrl()
    .then(async cdnUrl => {
      if (cdnUrl !== originalUrl) {
        getAppStore().updateWardrobeItemUrl(itemId, cdnUrl);
        if (originalUrl.startsWith('blob:')) URL.revokeObjectURL(originalUrl);
      }

      if (skipThumbnailProcessing) return;

      const processedUrl = await processItemThumbnail(cdnUrl);
      if (processedUrl) {
        getAppStore().setWardrobeItemThumbnail(itemId, processedUrl);
      }
    })
    .catch(() => {
      getAppStore().removeUserWardrobeItem(itemId);
      showNotification(NotificationType.generationError);
    })
    .finally(() => {
      getAppStore().setWardrobeItemProcessing(itemId, false);
    });
}

/**
 * Adds a user wardrobe item from an image URL and runs the CDN/thumbnail
 * pipeline. Shared by the picker flow and the deeplink seeding; callers are
 * responsible for any subscription gating. Returns the new item.
 */
export function importWardrobeItemFromUrl(url: string, categoryId: WardrobeCategory): WardrobeItem {
  const itemId = `imported-${Date.now()}`;
  const newItem: WardrobeItem = {
    id: itemId,
    name: 'Imported Item',
    thumbnailUrl: url,
    imageUrl: url,
    category: categoryId,
    isUserUploaded: true,
    isProcessing: true,
  };
  getAppStore().addUserWardrobeItem(newItem, categoryId);
  runUploadPipeline(itemId, () => uploadUrlToCdn(url), url, categoryId === WardrobeCategory.CompleteOutfits);
  return newItem;
}

export interface UseWardrobeUploadResult extends UseImageSourcePickerResult {
  /** Direct file handler for drag-and-drop */
  handleDroppedFile: (file: File) => void;
}

/**
 * Shared hook for wardrobe item upload from any source (Device, Drive, Canvas, Drag-and-Drop).
 * Uploads to CDN for session permanence, then runs an i2i pass on the thumbnail
 * to match the flat-lay style of preset items — except for Complete Outfits, where
 * the user's original photo is kept as-is.
 */
export function useWardrobeUpload(categoryId: WardrobeCategory | null): UseWardrobeUploadResult {
  const { validateAndProceed } = useSubscribe();
  const skipThumbnailProcessing = categoryId === WardrobeCategory.CompleteOutfits;

  const handleFile = useCallback(
    async (file: File) => {
      if (!categoryId) return;
      analytics.screenAction({ action: ScreenActionType.UPLOAD });

      const canProceed = await validateAndProceed();
      if (!canProceed) return;

      const localUrl = URL.createObjectURL(file);
      const itemId = `uploaded-${Date.now()}`;
      const name = file.name.replace(/\.[^/.]+$/, '');

      const newItem: WardrobeItem = {
        id: itemId,
        name,
        thumbnailUrl: localUrl,
        imageUrl: localUrl,
        category: categoryId,
        isUserUploaded: true,
        isProcessing: true,
      };
      getAppStore().addUserWardrobeItem(newItem, categoryId);
      runUploadPipeline(itemId, () => uploadFileToCdn(file), localUrl, skipThumbnailProcessing);
    },
    [categoryId, validateAndProceed, skipThumbnailProcessing],
  );

  const handleImageUrl = useCallback(
    async (url: string) => {
      if (!categoryId) return;
      analytics.screenAction({ action: ScreenActionType.UPLOAD });

      const canProceed = await validateAndProceed();
      if (!canProceed) return;

      importWardrobeItemFromUrl(url, categoryId);
    },
    [categoryId, validateAndProceed],
  );

  const pickerResult = useImageSourcePicker({ onImageUrl: handleImageUrl, onFile: handleFile });

  return {
    ...pickerResult,
    handleDroppedFile: handleFile,
  };
}
